import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  useGetSolarLeadQuery, 
  useDeleteSolarLeadMutation,
  useUpdateSolarLeadStatusMutation,
  useScheduleSolarMeetingMutation,
  useUpdateSolarMeetingMutation,
  useUpdateSolarLeadNotesMutation
} from '../../store/slices/apiSlice';
import { 
  ArrowLeft,
  Sun,
  User, 
  Phone, 
  Mail,
  Calendar,
  MapPin,
  Building2,
  CheckCircle,
  AlertCircle,
  XCircle,
  Clock,
  FileText,
  Trash2,
  Video,
  PhoneCall,
  Users
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import toast from 'react-hot-toast';

const SolarLeadDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<'new' | 'contacted' | 'meeting_scheduled' | 'converted' | 'not_interested'>('new');
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingType, setMeetingType] = useState<'call' | 'video' | 'in_person'>('call');
  const [notes, setNotes] = useState('');

  // Validate ID before making API call
  const leadId = id ? parseInt(id) : null;
  const isValidId = leadId && !isNaN(leadId) && leadId > 0;

  // Fetch solar lead details
  const { 
    data: leadData, 
    isLoading, 
    error, 
    refetch 
  } = useGetSolarLeadQuery(leadId!, {
    skip: !isValidId,
    refetchOnMountOrArgChange: true
  });

  // Mutations
  const [updateStatus, { isLoading: isUpdatingStatus }] = useUpdateSolarLeadStatusMutation();
  const [scheduleMeetingMutation, { isLoading: isScheduling }] = useScheduleSolarMeetingMutation();
  const [updateMeetingMutation, { isLoading: isUpdatingMeeting }] = useUpdateSolarMeetingMutation();
  const [updateNotesMutation, { isLoading: isUpdatingNotes }] = useUpdateSolarLeadNotesMutation();
  const [deleteSolarLead, { isLoading: isDeleting }] = useDeleteSolarLeadMutation();

  const lead = leadData?.data;
  const isUpdating = isUpdatingStatus || isScheduling || isUpdatingMeeting || isUpdatingNotes;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return <Badge variant="warning" className="flex items-center gap-1"><AlertCircle className="w-3 h-3" />New</Badge>;
      case 'contacted':
        return <Badge variant="info" className="flex items-center gap-1"><Clock className="w-3 h-3" />Contacted</Badge>;
      case 'meeting_scheduled':
        return <Badge variant="info" className="flex items-center gap-1"><Calendar className="w-3 h-3" />Meeting Scheduled</Badge>;
      case 'converted':
        return <Badge variant="success" className="flex items-center gap-1"><CheckCircle className="w-3 h-3" />Converted</Badge>;
      case 'not_interested':
        return <Badge variant="danger" className="flex items-center gap-1"><XCircle className="w-3 h-3" />Not Interested</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string | Date | null | undefined) => formatDateTimeUtil(dateString);;

  const handleUpdateStatus = async () => {
    if (!lead) return;

    try {
      await updateStatus({
        lead_id: lead.lead_id,
        status: selectedStatus,
        notes: notes.trim() || null
      }).unwrap();
      
      toast.success('Status updated successfully');
      setShowStatusModal(false);
      setShowNotesModal(false);
      setNotes('');
      refetch();
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to update status');
    }
  };

  const handleScheduleMeeting = async () => {
    if (!lead || !meetingDate) {
      toast.error('Please select a meeting date');
      return;
    }

    try {
      await scheduleMeetingMutation({
        lead_id: lead.lead_id,
        meeting_type: meetingType,
        meeting_date: meetingDate,
        meeting_notes: notes.trim() || null
      }).unwrap();
      
      toast.success('Meeting scheduled successfully');
      setShowMeetingModal(false);
      setMeetingDate('');
      setNotes('');
      refetch();
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to schedule meeting');
    }
  };

  const handleAddNotes = async () => {
    if (!lead) return;

    try {
      await updateNotesMutation({
        lead_id: lead.lead_id,
        notes: notes.trim()
      }).unwrap();
      
      toast.success('Notes added successfully');
      setShowNotesModal(false);
      setNotes('');
      refetch();
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to add notes');
    }
  };

  const handleDeleteLead = async () => {
    if (!lead) return;

    try {
      await deleteSolarLead(lead.lead_id).unwrap();
      toast.success('Solar lead deleted successfully');
      navigate('/admin/solar-leads');
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to delete solar lead');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/solar-leads')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Leads
          </Button>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/solar-leads')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Leads
          </Button>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Solar Lead Not Found</h3>
          <p className="text-gray-600 mb-4">
            The solar lead you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={() => navigate('/admin/solar-leads')}>
            Back to Leads
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/solar-leads')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Leads
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Solar Lead #{lead.lead_id}</h1>
            <p className="text-gray-600">Submitted on {formatDate(lead.created_at)}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {getStatusBadge(lead.status)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Information */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Contact Information</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-900">{lead.name}</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-900">{lead.email}</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-900">{lead.phone}</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Property Type</label>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-900">{lead.property_type}</span>
                </div>
              </div>
            </div>
          </Card>

          {/* Location Information */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Location</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-900">{lead.country}</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-900">{lead.state}</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Local Government Area</label>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-900">{lead.local_government_area}</span>
                </div>
              </div>
            </div>
          </Card>

          {/* Message */}
          {lead.message && (
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Message</h2>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-900 whitespace-pre-wrap leading-relaxed">
                  {lead.message}
                </p>
              </div>
            </Card>
          )}

          {/* Meeting Information */}
          {lead.meeting_date && (
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-purple-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Scheduled Meeting</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Meeting Date</label>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-900">{formatDate(lead.meeting_date)}</span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Meeting Type</label>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-900 capitalize">{lead.meeting_type?.replace('_', ' ')}</span>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
            <div className="space-y-3">
              <Button
                onClick={() => {
                  setSelectedStatus('contacted');
                  setShowStatusModal(true);
                }}
                className="w-full flex items-center gap-2"
                disabled={lead.status === 'contacted' || isUpdating}
              >
                <PhoneCall className="w-4 h-4" />
                Mark as Contacted
              </Button>
              
              <Button
                variant="outline"
                onClick={() => {
                  setMeetingDate('');
                  setMeetingType('call');
                  setShowMeetingModal(true);
                }}
                className="w-full"
                disabled={isUpdating}
              >
                <Calendar className="w-4 h-4 mr-2" />
                Schedule Meeting
              </Button>

              <Button
                variant="outline"
                onClick={() => {
                  setSelectedStatus(lead.status);
                  setShowStatusModal(true);
                }}
                className="w-full"
                disabled={isUpdating}
              >
                Change Status
              </Button>

              <Button
                variant="outline"
                onClick={() => {
                  setNotes(lead.notes || '');
                  setShowNotesModal(true);
                }}
                className="w-full"
                disabled={isUpdating}
              >
                <FileText className="w-4 h-4 mr-2" />
                Add Notes
              </Button>

              <Button
                variant="outline"
                onClick={() => setShowDeleteModal(true)}
                className="w-full text-red-600 hover:text-red-700 hover:border-red-300"
                disabled={isUpdating || isDeleting}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Lead
              </Button>
            </div>
          </Card>

          {/* Notes */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Notes</h3>
            {lead.notes ? (
              <div className="bg-yellow-50 p-3 rounded-lg">
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{lead.notes}</p>
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">No notes added yet</p>
            )}
          </Card>

          {/* Technical Details */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Details</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Submitted</label>
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-900">{formatDate(lead.created_at)}</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-900">{formatDate(lead.updated_at)}</span>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Status Update Modal */}
      <Modal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        title="Update Status"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="meeting_scheduled">Meeting Scheduled</option>
              <option value="converted">Converted</option>
              <option value="not_interested">Not Interested</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes (Optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Add any notes about this status change..."
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowStatusModal(false);
                setNotes('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateStatus}
              disabled={isUpdating}
            >
              {isUpdating ? 'Updating...' : 'Update Status'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Meeting Schedule Modal */}
      <Modal
        isOpen={showMeetingModal}
        onClose={() => setShowMeetingModal(false)}
        title="Schedule Meeting"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Meeting Date & Time *</label>
            <input
              type="datetime-local"
              value={meetingDate}
              onChange={(e) => setMeetingDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Meeting Type *</label>
            <select
              value={meetingType}
              onChange={(e) => setMeetingType(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="call">Phone Call</option>
              <option value="video">Video Call</option>
              <option value="in_person">In Person</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes (Optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Add meeting notes..."
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowMeetingModal(false);
                setMeetingDate('');
                setNotes('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleScheduleMeeting}
              disabled={isUpdating || !meetingDate}
            >
              {isUpdating ? 'Scheduling...' : 'Schedule Meeting'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Notes Modal */}
      <Modal
        isOpen={showNotesModal}
        onClose={() => setShowNotesModal(false)}
        title="Add Notes"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Add notes about this lead..."
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowNotesModal(false);
                setNotes('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddNotes}
              disabled={isUpdating || !notes.trim()}
            >
              {isUpdating ? 'Adding...' : 'Add Notes'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Solar Lead"
        size="md"
      >
        {lead && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Confirm Deletion</h3>
                <p className="text-sm text-gray-600">This action cannot be undone.</p>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Lead Details:</h4>
              <div className="space-y-1 text-sm text-gray-600">
                <p><span className="font-medium">Name:</span> {lead.name}</p>
                <p><span className="font-medium">Email:</span> {lead.email}</p>
                <p><span className="font-medium">Phone:</span> {lead.phone}</p>
                <p><span className="font-medium">Status:</span> {lead.status}</p>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteLead}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Lead
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SolarLeadDetails;
